/**
 * Renders the main admin app, which includes the navigation menu including the routes.
 * The content shown when clicking at the menu items is referred to as 'Sections'
 */

import React from "react"
import {BrowserRouter as Router, Switch} from 'react-router-dom'
import clsx from 'clsx';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Container from '@mui/material/Container';

import {useStyles} from 'components/styles/App.css'
import AccountInfo from 'components/commons/account/AccountInfo';
import CurrentAppbarContent from 'components/commons/routing/appbar/CurrentAppbarContent';
import PrivateRoute from "components/commons/routing/routes/PrivateRoute";
import GameSessionRedirect from "components/game/sections/session/redirect/GameSessionRedirect";
import GameSessionSection from "components/game/sections/session/section/GameSessionSection";
import GameNavItemsSecondary from "components/game/navigation/navItems/GameNavItemsSecondary";
import GameNavItemsPrimary from "components/game/navigation/navItems/GameNavItemsPrimary";
import AboutSection from "components/commons/sections/about/AboutSection";
import HelpSection from "components/commons/sections/help/HelpSection";
import ProfileOverviewSection from "components/commons/sections/profile/overview/ProfileOverviewSection";
import ChangePasswordSection from "../../../commons/sections/profile/changePassword/ChangePasswordSection";
import EditProfileSection from "../../../commons/sections/profile/edit/EditProfileSection";
import PlayerCoursesSection from "../../sections/courses/PlayerCoursesSection";
import DashboardGate from "../../../admin/sections/dashboard/gate/DashboardGate";

function GameApp() {
    // Styles for the general administration inteface
    const classes = useStyles();

    // Open and closing of the navigation drawer on the very left side
    const [open, setOpen] = React.useState(true);

    const handleDrawerOpen = () => {
        setOpen(true);
    };
    const handleDrawerClose = () => {
        setOpen(false);
    };

    // Main component for the admin interface - also includes the admin-specific routes
    return (
        <Router>
            <div className={classes.root}>

                {/* Visualization and handling of the header bar */}
                <AppBar position="absolute" className={clsx(classes.appBar, open && classes.appBarShift)}>
                    <Toolbar className={classes.toolbar}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            className={clsx(classes.menuButton, open && classes.menuButtonHidden)}
                            size="large">
                            <MenuIcon/>
                        </IconButton>
                        <Typography component="h1" variant="h6" color="inherit" noWrap className={classes.title}>
                            <CurrentAppbarContent/>
                        </Typography>
                        <AccountInfo/>
                    </Toolbar>
                </AppBar>

                {/* Visualization and handling of the drawer including the navigation items */}
                <Drawer variant="permanent"
                        classes={{
                            paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
                        }}
                        open={open}
                >
                    <div className={classes.toolbarIcon}>
                        <IconButton onClick={handleDrawerClose} size="large">
                            <ChevronLeftIcon/>
                        </IconButton>
                    </div>
                    <Divider/>
                    <GameNavItemsPrimary/>
                    <Divider/>
                    <GameNavItemsSecondary/>
                </Drawer>

                {/* Visualization and handling of the routes leading to the respective content sections */}
                <main className={classes.content}>
                    <div className={classes.appBarSpacer}/>
                    <Container maxWidth={false} className={classes.container}>
                        {/* Routes navigation */}
                        <Switch>
                            {/* Primary navigation items */}
                            <PrivateRoute exact path={'/game/dashboard'} allowedRoles={['player']}
                                          component={DashboardGate}/>

                            <PrivateRoute exact path={'/common/profile'} allowedRoles={['player']}
                                          component={ProfileOverviewSection}/>
                            <PrivateRoute path={'/common/profile/edit'} allowedRoles={['player']}
                                          component={EditProfileSection}/>
                            <PrivateRoute path={'/common/profile/changePassword'} allowedRoles={['player']}
                                          component={ChangePasswordSection}/>

                            {/* Secondary navigation items */}
                            <PrivateRoute path={'/common/about'} allowedRoles={['player']} component={AboutSection}/>
                            <PrivateRoute path={'/common/help'} allowedRoles={['player']} component={HelpSection}/>

                            {/* Navigation to the game session */}
                            <PrivateRoute path={'/game/session/:scoredSceneKey/play'} allowedRoles={['player']}
                                          component={GameSessionSection}/>
                            <PrivateRoute path={'/game/session'} allowedRoles={['player']}
                                          component={GameSessionRedirect}/>

                            {/* Navigation to player courses */}
                            <PrivateRoute path={'/game/courses'} allowedRoles={['player']}
                                          component={PlayerCoursesSection}/>
                        </Switch>
                    </Container>
                </main>
            </div>
        </Router>
    );
}


export default GameApp;